import styled from "@emotion/styled";
import { motion } from "framer-motion";
import React from "react";
import { useTheme } from "../context/ThemeContext";
import { darkTheme, lightTheme } from "../styles/theme";
import { RandomFloatingIcons, DEVOPS_ICONS } from "./TechStackIcons";

const HobbiesSection = styled.section<{ theme: "light" | "dark" }>`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 70px 5% 40px;
  position: relative;
  overflow: hidden;
  background: ${({ theme }) =>
    theme === "light" ? "#ffffff" : darkTheme.background};

  /* Checkerboard pattern */
  background-image: ${({ theme }) =>
    theme === "light"
      ? `
        linear-gradient(45deg, rgba(77, 159, 255, 0.12) 25%, transparent 25%),
        linear-gradient(-45deg, rgba(77, 159, 255, 0.12) 25%, transparent 25%),
        linear-gradient(45deg, transparent 75%, rgba(77, 159, 255, 0.12) 75%),
        linear-gradient(-45deg, transparent 75%, rgba(77, 159, 255, 0.12) 75%)
      `
      : `
        linear-gradient(45deg, rgba(255, 255, 255, 0.03) 25%, transparent 25%),
        linear-gradient(-45deg, rgba(255, 255, 255, 0.03) 25%, transparent 25%),
        linear-gradient(45deg, transparent 75%, rgba(255, 255, 255, 0.03) 75%),
        linear-gradient(-45deg, transparent 75%, rgba(255, 255, 255, 0.03) 75%)
      `};
  background-size: 40px 40px;
  background-position:
    0 0,
    0 20px,
    20px -20px,
    -20px 0px;

  /* Bottom-right accent overlay */
  &::before {
    content: "";
    position: absolute;
    bottom: 0;
    right: 0;
    width: 45%;
    height: 45%;
    background: ${({ theme }) =>
      theme === "light"
        ? `radial-gradient(ellipse at bottom right, rgba(77, 159, 255, 0.15), transparent 70%)`
        : `radial-gradient(ellipse at bottom right, ${darkTheme.accent}10, transparent 70%)`};
    pointer-events: none;
    z-index: 0;
  }

  @media (min-width: 768px) {
    padding: 70px 10% 40px;
    background-size: 50px 50px;
    background-position:
      0 0,
      0 25px,
      25px -25px,
      -25px 0px;
  }
`;

const ContentWrapper = styled(motion.div)`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled(motion.h2)<{ theme: "light" | "dark" }>`
  display: inline-block;
  font-size: 1.5rem;
  margin-bottom: 1.2rem;
  padding: 8px 16px;
  background: ${({ theme }) =>
    theme === "light" ? lightTheme.accent : darkTheme.accent};
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.background : darkTheme.background};
  border: 4px solid
    ${({ theme }) => (theme === "light" ? lightTheme.border : darkTheme.border)};
  box-shadow: ${({ theme }) =>
    theme === "light" ? lightTheme.shadow : darkTheme.shadow};

  @media (min-width: 768px) {
    font-size: 1.8rem;
  }
`;

const HobbiesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  margin-top: 1.2rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const HobbyCard = styled(motion.div)<{ theme: "light" | "dark" }>`
  background: ${({ theme }) =>
    theme === "light" ? lightTheme.surface : darkTheme.surface};
  padding: 1.2rem;
  border: 4px solid
    ${({ theme }) => (theme === "light" ? lightTheme.border : darkTheme.border)};
  box-shadow: ${({ theme }) =>
    theme === "light" ? lightTheme.shadow : darkTheme.shadow};
  transition: transform 0.15s ease;

  &:hover {
    transform: translate(-3px, -3px);
  }
`;

const HobbyTitle = styled.h3<{ theme: "light" | "dark" }>`
  display: inline-block;
  font-size: 1rem;
  margin-bottom: 0.7rem;
  padding: 6px 12px;
  background: ${({ theme }) =>
    theme === "light" ? lightTheme.accent : darkTheme.accent};
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.background : darkTheme.background};
  border: 3px solid
    ${({ theme }) => (theme === "light" ? lightTheme.border : darkTheme.border)};
  box-shadow: ${({ theme }) =>
    theme === "light" ? lightTheme.shadow : darkTheme.shadow};
`;

const HobbyDescription = styled.p<{ theme: "light" | "dark" }>`
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.textSecondary : darkTheme.textSecondary};
  line-height: 1.5;
  margin-bottom: 0.7rem;
  font-size: 0.85rem;

  @media (min-width: 768px) {
    font-size: 0.9rem;
  }
`;

const HobbyLink = styled(motion.a)<{ theme: "light" | "dark" }>`
  display: inline-block;
  margin-top: 0.4rem;
  padding: 6px 10px;
  background: ${({ theme }) =>
    theme === "light" ? lightTheme.accent : darkTheme.accent};
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.background : darkTheme.background};
  text-decoration: none;
  font-weight: 800;
  font-size: 0.8rem;
  border: 3px solid
    ${({ theme }) => (theme === "light" ? lightTheme.border : darkTheme.border)};
  box-shadow: ${({ theme }) =>
    theme === "light" ? lightTheme.shadow : darkTheme.shadow};
  transition: transform 0.15s ease;

  &:hover {
    transform: translate(-2px, -2px);
  }

  @media (min-width: 768px) {
    font-size: 0.85rem;
  }
`;

const Hobbies: React.FC = () => {
  const { theme } = useTheme();
  const hobbies = [
    {
      title: "Reading",
      description:
        "I love exploring new ideas through books, particularly in technology, science, and philosophy.",
      link: {
        text: "You can find my tiny library here",
        to: "/library",
      },
    },
    {
      title: "Tarot",
      description:
        "I enjoy tarot as a personal hobby, and I built Aurum Tarot as a small space for reflection, reading practice, and experimentation.",
      link: {
        text: "Explore Aurum Tarot",
        to: "https://tarot.lionet.vn",
      },
    },
    {
      title: "Sport",
      description:
        "I enjoy many sports — badminton, swimming, running, cycling, and pickleball — which help me stay active and improve my focus and coordination.",
    },
    {
      title: "Learning",
      description:
        "Constantly expanding my knowledge through online courses and technical documentation.",
      link: {
        text: "Explore My Blog",
        to: "https://blog.lionet.vn",
      },
    },
  ];

  return (
    <HobbiesSection id="hobbies" theme={theme}>
      {/* Floating Tech Stack Icons */}
      <RandomFloatingIcons theme={theme} icons={DEVOPS_ICONS} count={3} />

      <ContentWrapper
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <SectionTitle theme={theme}>Hobbies & Interests</SectionTitle>
        <HobbiesGrid>
          {hobbies.map((hobby, index) => (
            <HobbyCard
              key={hobby.title}
              theme={theme}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <HobbyTitle theme={theme}>{hobby.title}</HobbyTitle>
              <HobbyDescription theme={theme}>
                {hobby.description}
              </HobbyDescription>
              {hobby.link && (
                <HobbyLink
                  theme={theme}
                  href={hobby.link.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3, delay: 0.2 }}
                >
                  {hobby.link.text} →
                </HobbyLink>
              )}
            </HobbyCard>
          ))}
        </HobbiesGrid>
      </ContentWrapper>
    </HobbiesSection>
  );
};

export default Hobbies;
